// The till's list of its shop's tables (UC-39).
//
// A table is not menu: it rides on neither the menu read nor its poll, so an
// owner's add, rename or removal reaches the till at its next read of this
// list. The till reads it after each unlock, each time Pick a table opens,
// and again after a checkout refused for its table. It shows the list it has
// at once and replaces it when the answer comes.

import { api } from '../api/client';
import type { components } from '../api/types';
import { currentDraft, followTables } from '../draft/draft.svelte';
import { problemOf } from './answer';

export type Table = components['schemas']['TableBody'];

/** Where the last read stands: on its way, answered, or failed. */
export type TablesRead = 'loading' | 'ok' | 'error';

let tables = $state<Table[]>([]);
let read = $state<TablesRead>('loading');
// Reads can overlap, a refusal's and the sheet's, and answer out of order.
// Only the latest one asked may replace the list, so an older answer never
// takes a table the cashier just picked off the cart.
let asked = 0;

/** The shop's active tables in name order, as the server sends them. */
export function currentTables(): Table[] {
	return tables;
}

/** Where the last read of the list stands. */
export function tablesRead(): TablesRead {
	return read;
}

/**
 * Whether a dine-in in this shop names a table: the shop has at least one.
 * The rule is the server's (TABLE_REQUIRED); the till asks for one up front so
 * the cashier is not refused at Pay. While the first read is on its way or
 * failed the till cannot know, so it asks rather than send a dine-in the
 * server may refuse (the Till page, T15).
 */
export function shopHasTables(): boolean {
	return tables.length > 0 || read !== 'ok';
}

/**
 * The table the cart names: as the till's list gives it, or, while no read
 * has answered, as the cart kept it, so a cart a restart brought back sells
 * at its table; the server judges the table either way. Null for none.
 */
export function cartTable(): Table | null {
	const draft = currentDraft();
	if (!draft.tableId) return null;
	const listed = tables.find((t) => t.id === draft.tableId);
	if (listed) return listed;
	return read === 'ok' ? null : { id: draft.tableId, name: draft.tableName };
}

/** Whether the cart is a dine-in that still has to be told its table. */
export function tableNeeded(): boolean {
	return currentDraft().type === 'dine_in' && shopHasTables() && cartTable() === null;
}

/**
 * Read the list. A failure keeps the list in hand, as the menu does. A 404
 * that names no field is an API older than tables, which a revert put back:
 * that shop has no tables to offer, and a cart's table comes off, so a
 * dine-in goes with none, which that API takes. True when the cart's table
 * came off because the owner removed it.
 */
export async function loadTables(): Promise<boolean> {
	const ask = ++asked;
	if (tables.length === 0 && read !== 'ok') read = 'loading';
	try {
		const { data, error, response } = await api.GET('/api/till/tables');
		if (ask !== asked) return false;
		if (data && error === undefined) {
			tables = data.tables ?? [];
			read = 'ok';
			return followTables(tables);
		}
		if (response.status === 404 && !problemOf(error)?.details?.path) {
			tables = [];
			read = 'ok';
			return followTables(tables);
		}
	} catch {
		// No answer: the list in hand stays, and the next read decides.
		if (ask !== asked) return false;
	}
	read = 'error';
	return false;
}
